import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import { usePageSeo } from '../../hooks/usePageSeo.js';
import { stripHtmlToText } from '../../utils/seoMeta.js';
import { useVisibleLoading } from '../../hooks/useVisibleLoading.js';

const bodyProse =
  'max-w-none space-y-5 text-lg leading-relaxed text-slate-700 [&_h1]:font-display [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-[#0d2b7f] [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-[#0d2b7f] [&_h3]:text-xl [&_h3]:font-semibold [&_li]:ml-4 [&_li]:text-lg [&_li]:leading-relaxed [&_p]:text-lg [&_p]:leading-relaxed [&_p]:text-slate-700 [&_a]:text-accent-lime [&_a]:underline hover:[&_a]:text-[#0d2b7f] [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-lg [&_img]:object-contain';

export default function NewsDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const showLoading = useVisibleLoading(loading);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setNotFound(false);
      setError(null);
      try {
        const { data: res } = await api.get(`/v1/public/noticias/${id}`);
        if (cancelled) return;
        setData(res?.data ?? null);
      } catch (e) {
        if (cancelled) return;
        if (e.status === 404) setNotFound(true);
        else setError(e.message || 'No se pudo cargar la noticia');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (id) load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const html = (data?.contenido && String(data.contenido).trim()) || '';

  const fechaLabel =
    data?.fecha_publicacion &&
    new Date(data.fecha_publicacion).toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  const heroTitle = showLoading
    ? 'Cargando…'
    : data?.titulo || (notFound ? 'Noticia no encontrada' : 'Noticia');

  usePageSeo(
    showLoading || notFound
      ? { title: notFound ? 'Noticia no encontrada' : 'Noticia' }
      : data
        ? {
            title: data.titulo,
            description: data.resumen || stripHtmlToText(data.contenido),
            image: data.imagen ? assetUrl(data.imagen) : undefined,
            type: 'article',
          }
        : null
  );

  const heroDescription = showLoading
    ? ''
    : data?.resumen ||
      (notFound
        ? 'Esta noticia no está disponible o ya no se publica.'
        : error || '');

  return (
    <div className="pb-20">
      <PublicPageHero
        backTo="/noticias"
        backLabel="Volver a noticias"
        eyebrow={data?.categoria || 'Noticias'}
        title={heroTitle}
        description={heroDescription}
        descriptionClassName="mt-5 text-xl leading-relaxed text-slate-200 md:text-2xl"
      />

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
      {showLoading && (
        <BrandLoading variant="inline" tone="light" label="Cargando noticia…" className="min-h-[32rem]" />
      )}

      {!showLoading && error && !data && <p className="text-slate-600">{error}</p>}

      {!showLoading && data && (
        <article>
          <PremiumCard variant="light" className="overflow-hidden !p-6 sm:!p-8">
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-10 lg:items-start">
              <div className="min-w-0 lg:col-span-5">
                {fechaLabel ? (
                  <div className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-600">
                    <Calendar className="h-4 w-4 shrink-0 text-accent-lime" aria-hidden />
                    <time dateTime={data.fecha_publicacion}>{fechaLabel}</time>
                  </div>
                ) : null}

                <div className="relative w-full max-w-md overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200 lg:max-w-none">
                  {data.imagen ? (
                    <img
                      src={assetUrl(data.imagen)}
                      alt={data.titulo || 'Imagen de la noticia'}
                      className="mx-auto block h-auto w-full max-h-[min(36rem,70vh)] object-contain"
                      decoding="async"
                    />
                  ) : (
                    <div className="flex min-h-[14rem] items-center justify-center px-6 text-sm font-medium uppercase tracking-wider text-slate-500">
                      Sin imagen
                    </div>
                  )}
                  {data.categoria ? (
                    <span className="absolute left-3 top-3 rounded-full bg-brand-navy px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white ring-1 ring-[#1acb12]/45">
                      {data.categoria}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="min-w-0 lg:col-span-7">
                {html ? (
                  <div className={bodyProse} dangerouslySetInnerHTML={{ __html: html }} />
                ) : (
                  <p className="text-lg text-slate-500">Esta noticia no tiene contenido adicional.</p>
                )}
              </div>
            </div>
          </PremiumCard>
        </article>
      )}
        </div>
      </section>
    </div>
  );
}
